import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Star, ArrowLeft, ShieldAlert } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-[#E6DFD3] py-3 text-[#2D231E] font-sans focus:outline-none focus:border-[#4A3C31] transition-colors rounded-none";
const labelCls =
  "block text-[11px] font-sans tracking-[0.25em] uppercase text-[#5D4037] mb-2";

export default function ModerateFeedbackPage() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const token = search.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", role: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/feedback/moderate/${id}`, { params: { token } });
        setData(r.data);
        setForm({
          name: r.data.published_name || r.data.name,
          role: r.data.published_role || r.data.role || "",
          message: r.data.published_message || r.data.message,
        });
      } catch (e) {
        if (e?.response?.status === 403) setForbidden(true);
        else setError("Couldn't load this feedback.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token]);

  const onPublish = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/moderate/${id}/publish`, form, { params: { token } });
      setSubmitted(true);
    } catch {
      setError("Couldn't publish. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onUnpublish = async () => {
    setError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/moderate/${id}/unpublish`, null, { params: { token } });
      setData({ ...data, published: false });
      setSubmitted(false);
    } catch {
      setError("Couldn't unpublish. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <main className="max-w-3xl mx-auto px-6 sm:px-12 py-24 text-center">
      <Loader2 size={22} className="animate-spin text-[#8B7355] mx-auto" />
    </main>
  );

  if (forbidden) return (
    <main className="max-w-3xl mx-auto px-6 sm:px-12 py-24">
      <ShieldAlert size={32} className="text-[#9D5B5B] mb-4" strokeWidth={1.5} />
      <h1 className="font-serif text-3xl text-[#2D231E] mb-2">Link expired or invalid.</h1>
      <p className="font-sans text-sm text-[#5D4037]">This moderation link can't be verified. Check the original email — each link is unique.</p>
      <Link to="/" className="mt-6 inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-[#4A3C31] hover:text-[#2D231E]"><ArrowLeft size={14}/> Back to home</Link>
    </main>
  );

  if (!data) return (
    <main className="max-w-3xl mx-auto px-6 sm:px-12 py-24">
      <p className="font-sans text-sm text-[#5D4037]">{error || "Not found."}</p>
    </main>
  );

  return (
    <main data-testid="moderate-page" className="max-w-3xl mx-auto px-6 sm:px-12 py-16 sm:py-24">
      <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-3">Moderate feedback</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#2D231E] tracking-tight leading-tight mb-3">
        Review &amp; publish.
      </h1>
      <p className="font-sans text-sm sm:text-base text-[#5D4037] mb-10 max-w-xl">
        Edit any typos or wording, then publish — the testimonial will appear on
        the landing page within seconds. Or close this tab to leave it private.
      </p>

      {/* Original */}
      <div className="bg-[#F9F6F0] border border-[#E6DFD3] p-6 sm:p-7 rounded-sm mb-10">
        <div className="flex items-center gap-1 mb-3">
          {[1,2,3,4,5].map(n => (
            <Star key={n} size={16} strokeWidth={1.5} className={n <= data.rating ? "text-[#8B7355] fill-[#8B7355]" : "text-[#C4A484]"} />
          ))}
          <span className="ml-2 font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B7355]">Original submission</span>
        </div>
        <blockquote className="font-serif text-lg italic text-[#2D231E] leading-snug">&ldquo;{data.message}&rdquo;</blockquote>
        <p className="font-sans text-xs text-[#8B7355] mt-3">
          {data.name}{data.role ? ` · ${data.role}` : ""} {data.email ? `· ${data.email}` : ""}
          {data.consent_to_publish ? " · Consented to publish" : " · No publication consent given"}
        </p>
      </div>

      {/* Editable */}
      <form onSubmit={onPublish} data-testid="moderate-form" className="bg-[#FAF9F6] border border-[#E6DFD3] p-7 sm:p-9 rounded-sm">
        <p className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#5D4037] mb-5">Polished version (what visitors will see)</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
          <div>
            <label className={labelCls} htmlFor="m-name">Display name</label>
            <input id="m-name" data-testid="m-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <label className={labelCls} htmlFor="m-role">Role (optional)</label>
            <input id="m-role" data-testid="m-role" className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="m-message">Quote</label>
            <textarea id="m-message" data-testid="m-message" rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
          </div>
        </div>

        {error && <p className="mt-4 font-sans text-sm text-[#9D5B5B]">{error}</p>}

        {submitted ? (
          <div data-testid="moderate-success" className="mt-7 border border-[#E6DFD3] p-6 rounded-sm bg-[#F9F6F0] flex items-start gap-3">
            <CheckCircle2 size={22} strokeWidth={1.5} className="text-[#8B7355] mt-0.5" />
            <div>
              <p className="font-serif text-lg text-[#2D231E]">Published.</p>
              <p className="font-sans text-sm text-[#5D4037] mt-1">It's live on the landing page now. You can keep editing and re-saving, or unpublish below.</p>
            </div>
          </div>
        ) : null}

        <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
          {data.published ? (
            <button type="button" onClick={onUnpublish} disabled={submitting}
              className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#9D5B5B] hover:text-[#7A4040]">
              Unpublish
            </button>
          ) : <span />}
          <button type="submit" disabled={submitting} data-testid="moderate-publish"
            className="inline-flex items-center gap-2 px-7 py-3 bg-[#4A3C31] text-[#F9F6F0] font-sans text-[11px] tracking-[0.3em] uppercase hover:bg-[#2D231E] transition-colors rounded-sm disabled:opacity-60">
            {submitting && <Loader2 size={12} className="animate-spin" />}
            {data.published ? "Save Changes" : "Publish to Site"}
          </button>
        </div>
      </form>

      <div className="mt-8">
        <Link to="/" className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-[#4A3C31] hover:text-[#2D231E]">
          <ArrowLeft size={14}/> Back to home
        </Link>
      </div>
    </main>
  );
}
